import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Datasheet from 'react-datasheet';
import classnames from 'classnames';
import { Button } from 'semantic-ui-react';
import styled from 'styled-components';

import { MAX_SUBSCRIPTION_USER } from '@/constants';
import i18n from '@/settings/i18n';
import { isEMBA } from '@/types/subscriptions/subscription';
import { User, userFactory } from '@/types/user';
import { classifiedSort } from '@/utils/name';
import { LoaderWithDimmer } from '@/components/utils/Loader';
import { SpreadsheetUsage } from '../SpreadsheetUsage';
import { UsersDatasheet, GridElement, Selection } from '../UsersDatasheet';

import 'react-datasheet/lib/react-datasheet.css';

const COLUMN_SIZE = 5;
const leftmostCellClicked = ({ start }: Selection) =>
  start.i !== 0 && start.j === 0;
const topCellClicked = ({ start }: Selection) => start.i === 0 && start.j !== 0;
const topLeftCellClicked = ({ start }: Selection) =>
  start.i === 0 && start.j === 0;

type UserError = {
  firstName: string;
  lastName: string;
  kanaFirstName: string;
  kanaLastName: string;
  email: string;
};

export type Props = {
  errors?: any;
  serviceGroupId?: number | null;
  users: User[];
  setUsers: (users: User[]) => void;
  cellSelection: Selection | null;
  setCellSelection: (cellSelection: Selection) => void;
  onCellsChanged: (
    changes: Datasheet.CellsChangedArgs<any, any>,
    additions: Datasheet.CellAdditionsArgs<any> | undefined,
  ) => void;
  isPasting: boolean;
  datasheetRef: React.RefObject<any>;
  children: ReactNode;
};

export const NONGLSView: React.FC<Props> = ({
  errors = [],
  serviceGroupId,
  users = [],
  setUsers,
  cellSelection,
  setCellSelection,
  onCellsChanged,
  isPasting,
  datasheetRef,
  children,
}) => {
  const { t } = useTranslation();
  const hasErrors = errors.length !== 0;

  return (
    <>
      {isPasting && <LoaderWithDimmer />}
      <Styled>
        <div
          id="users"
          data-test="users-datasheet"
          className={classnames('field', { error: hasErrors })}
          ref={datasheetRef}
        >
          <label className="heading">
            {t('models.subscription.users')}
            <small>
              {serviceGroupId && isEMBA(serviceGroupId)
                ? t('ui.subscription.maximumNumberUsers.emba', {
                    count: MAX_SUBSCRIPTION_USER,
                  })
                : t('ui.subscription.maximumNumberUsers.hodai', {
                    count: MAX_SUBSCRIPTION_USER,
                  })}
            </small>
          </label>
          {/* component化しない */}
          <SpreadsheetUsage />
          <UsersDatasheet
            className="users"
            data={hasErrors ? generateGrid(users, errors) : generateGrid(users)}
            onSelect={(newSelection: Selection) => {
              if (leftmostCellClicked(newSelection)) {
                newSelection.end.j = COLUMN_SIZE;
              }
              if (topCellClicked(newSelection)) {
                newSelection.end.i = users.length;
              }
              if (topLeftCellClicked(newSelection)) {
                newSelection.end.i = users.length;
                newSelection.end.j = COLUMN_SIZE;
              }
              setCellSelection(newSelection);
            }}
            selected={cellSelection}
            onCellsChanged={onCellsChanged}
            overflow="clip"
            valueRenderer={(cell: GridElement) => cell.value}
          />
          <div className="under-table">
            <span className="add-line">
              <Button
                color="twitter"
                icon="plus"
                onClick={(e) => {
                  e.preventDefault();
                  setUsers([...users, userFactory()]);
                }}
                size="tiny"
              />
            </span>
          </div>
          {children}
        </div>
      </Styled>
    </>
  );
};

const generateGrid = (
  users: User[],
  errors: any | [UserError, undefined] = [],
) => {
  const grid = [
    [
      ...classifiedSort([
        {
          readOnly: true,
          value: i18n.t('models.user.firstName'),
          width: '12.5%',
        },
        {
          readOnly: true,
          value: i18n.t('models.user.lastName'),
          width: '12.5%',
        },
      ]),
      ...classifiedSort([
        {
          readOnly: true,
          value: i18n.t('models.user.kanaFirstName'),
          width: '17.5%',
        },
        {
          readOnly: true,
          value: i18n.t('models.user.kanaLastName'),
          width: '17.5%',
        },
      ]),
      {
        readOnly: true,
        value: i18n.t('models.user.email'),
        width: '35%',
      },
    ],
    ...users.map((user, i) => [
      ...classifiedSort([
        {
          className:
            errors[i] && errors[i].firstName ? 'user-cell error' : 'user-cell',
          value: user.firstName,
        },
        {
          className:
            errors[i] && errors[i].lastName ? 'user-cell error' : 'user-cell',
          value: user.lastName,
        },
      ]),
      ...classifiedSort([
        {
          className:
            errors[i] && errors[i].kanaFirstName
              ? 'user-cell error'
              : 'user-cell',
          value: user.kanaFirstName,
        },
        {
          className:
            errors[i] && errors[i].kanaLastName
              ? 'user-cell error'
              : 'user-cell',
          value: user.kanaLastName,
        },
      ]),
      {
        className:
          errors[i] && errors[i].email ? 'user-cell error' : 'user-cell',
        value: user.email,
      },
    ]),
  ];

  return grid.map((row, i) => [
    {
      className: 'index-cell',
      readOnly: true,
      value: i > 0 ? i : null,
      width: '5%',
    },
    ...row,
  ]);
};

const Styled = styled.div`
  .heading {
    display: flex;
    align-items: center;

    small {
      padding-left: 1em;
      color: #aaa;
    }
  }

  .hint {
    margin-bottom: 0.5em !important;
  }

  .data-grid.users {
    width: 100%;
    background-color: #fff;
  }

  .ui.twitter.button {
    background-color: #24387c !important;
  }

  .data-grid-container .data-grid .cell .value-viewer {
    overflow-x: hidden;
  }

  /**
 * Fix for IE. Prevent values in the editing cell from being hidden
 */
  .data-grid-container .users .cell .data-editor {
    height: 100%;
    padding: 0 0.33em;
    text-align: left;
  }

  .data-grid-container .users tr:first-child td.cell.read-only {
    padding: 0.66em 0.33em;
    background-color: #24387c !important;
    color: #fff;
    text-align: left;
  }

  .data-grid-container .users .cell.read-only {
    padding: 0.33em;
    background: #f2f2f2;
  }

  .index-cell .value-viewer {
    text-align: center;
  }

  .user-cell .value-viewer {
    padding: 0.33em;
    text-align: left;
  }

  .user-cell.error.cell {
    background: rgb(255, 204, 204);
  }

  .under-table {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }

  .add-line {
    margin: 0.4em 0;
    margin-left: auto;
    text-align: right;
  }

  .add-line .ui.icon.button {
    padding: 0.6em;
  }

  .error-list {
    line-height: 1.5em;
  }
`;
